/**
 * Lead Download Service
 * Located: frontend/src/features/leads/services/downloadService.js
 * Uses shared API client for consistent authentication and error handling
 */

import api from "../../../shared/api.jsx";
import {
  logInfo,
  logError,
} from "../../../shared/utils/logger.js";

class LeadDownloadService {
  /**
   * Make a binary request using the shared API's authentication and error handling
   * @param {string} url - Request URL
   * @param {Object} params - Query parameters
   * @param {string} acceptHeader - Accept header for the request
   * @returns {Promise<Response>} Fetch response
   */
  async makeBinaryRequest(
    url,
    params = {},
    acceptHeader = "application/octet-stream"
  ) {
    logInfo("BinaryRequest: starting via shared API", {
      url,
      params,
      acceptHeader,
    });

    try {
      // Use the shared API client's binary method
      const response = await api.binary(url, params, acceptHeader);

      logInfo("BinaryRequest: request successful", { url });
      return response;
    } catch (error) {
      logError("BinaryRequest: request failed", error);
      throw error;
    }
  }

  /**
   * Download lead as ZIP file
   * @param {string} leadId - Lead ID
   * @param {boolean} includeCustomerDetails - Whether to include customer details
   * @returns {Promise<void>}
   */
  async downloadLead(leadId, includeCustomerDetails = false) {
    try {
      const url = `/leads/download/${leadId}`;
      const params = { includeCustomerDetails };

      // Use the shared API's authentication and error handling for binary requests
      const response = await this.makeBinaryRequest(
        url,
        params,
        "application/zip"
      );

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `lead_${leadId}.zip`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      this.downloadBlob(blob, filename);
    } catch (error) {
      logError("LeadDownloadService: ZIP download failed", error);
      throw error;
    }
  }

  /**
   * Download lead PDF only
   * @param {string} leadId - Lead ID
   * @param {boolean} includeCustomerDetails - Whether to include customer details
   * @returns {Promise<void>}
   */
  async downloadPdf(leadId, includeCustomerDetails = false) {
    try {
      const url = `/leads/download/${leadId}/pdf`;
      const params = { includeCustomerDetails };

      // Use the shared API's authentication and error handling for binary requests
      const response = await this.makeBinaryRequest(
        url,
        params,
        "application/pdf"
      );

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = `lead_${leadId || "unknown"}_summary.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create blob and download
      const blob = await response.blob();
      this.downloadBlob(blob, filename);
    } catch (error) {
      logError("LeadDownloadService: PDF download failed", error);
      throw error;
    }
  }

  /**
   * Get download information for a lead
   * @param {string} leadId - Lead ID
   * @returns {Promise<Object>} Download info
   */
  async getDownloadInfo(leadId) {
    try {
      // Use the shared API for JSON responses
      const response = await api.get(`/leads/download/${leadId}/info`);
      return response;
    } catch (error) {
      console.error("Get download info failed:", error);
      throw error;
    }
  }

  /**
   * Download blob as file
   * @param {Blob} blob - Blob to download
   * @param {string} filename - Filename for download
   */
  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}

export const leadDownloadService = new LeadDownloadService();
export default leadDownloadService;
